
import { useState, useMemo } from 'react';
import { ChevronRight, ChevronDown, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface JsonViewerProps {
    data: unknown;
    className?: string;
    maxInitialDepth?: number;
    /** Callback when a truncated string is clicked, receives full string value and key name */
    onTruncatedStringClick?: (fullText: string, keyName?: string) => void;
}

/**
 * Interactive JSON viewer with expand/collapse and syntax highlighting.
 */
export function JsonViewer({ data, className, maxInitialDepth = 2, onTruncatedStringClick }: JsonViewerProps) {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    if (data === null || data === undefined) {
        return (
            <div className={cn('text-sm text-slate-500 italic', className)}>
                No data available
            </div>
        );
    }

    return (
        <div className={cn('relative', className)}>
            <Button
                variant="ghost"
                size="sm"
                className="absolute right-0 top-0 h-7 w-7 p-0"
                onClick={handleCopy}
                title="Copy JSON"
            >
                {copied ? (
                    <Check className="h-3.5 w-3.5 text-green-600" />
                ) : (
                    <Copy className="h-3.5 w-3.5" />
                )}
            </Button>
            <div className="overflow-auto rounded-md bg-slate-50 p-3 font-mono text-xs">
                <JsonNode value={data} depth={0} maxInitialDepth={maxInitialDepth} onTruncatedStringClick={onTruncatedStringClick} />
            </div>
        </div>
    );
}

interface JsonNodeProps {
    value: unknown;
    depth: number;
    maxInitialDepth: number;
    keyName?: string;
    onTruncatedStringClick?: (fullText: string, keyName?: string) => void;
}

function JsonNode({ value, depth, maxInitialDepth, keyName, onTruncatedStringClick }: JsonNodeProps) {
    const [isExpanded, setIsExpanded] = useState(depth < maxInitialDepth);

    const indent = depth * 30;

    // Render primitives
    if (value === null) {
        return (
            <JsonLine indent={indent} keyName={keyName}>
                <span className="text-slate-500">null</span>
            </JsonLine>
        );
    }

    if (value === undefined) {
        return (
            <JsonLine indent={indent} keyName={keyName}>
                <span className="text-slate-500">undefined</span>
            </JsonLine>
        );
    }

    if (typeof value === 'boolean') {
        return (
            <JsonLine indent={indent} keyName={keyName}>
                <span className="text-purple-600">{value.toString()}</span>
            </JsonLine>
        );
    }

    if (typeof value === 'number') {
        return (
            <JsonLine indent={indent} keyName={keyName}>
                <span className="text-blue-600">{value}</span>
            </JsonLine>
        );
    }

    if (typeof value === 'string') {
        // Truncate very long strings
        const isTruncated = value.length > 200;
        const displayValue = isTruncated 
            ? `${value.slice(0, 200)}...` 
            : value;
        
        const handleClick = isTruncated && onTruncatedStringClick 
            ? () => onTruncatedStringClick(value, keyName)
            : undefined;
        
        return (
            <JsonLine indent={indent} keyName={keyName}>
                <span 
                    className={cn(
                        "text-green-600",
                        isTruncated && onTruncatedStringClick && "cursor-pointer hover:bg-green-100 hover:text-green-800 rounded px-0.5 transition-colors"
                    )}
                    onClick={handleClick}
                    title={isTruncated ? "Click to view full text" : undefined}
                >
                    "{displayValue}"
                </span>
            </JsonLine>
        );
    }

    // Render arrays
    if (Array.isArray(value)) {
        if (value.length === 0) {
            return (
                <JsonLine indent={indent} keyName={keyName}>
                    <span className="text-slate-600">[]</span>
                </JsonLine>
            );
        }

        return (
            <div>
                <div
                    className="flex cursor-pointer items-center hover:bg-slate-100"
                    onClick={() => setIsExpanded(!isExpanded)}
                    style={{ paddingLeft: indent }}
                >
                    {isExpanded ? (
                        <ChevronDown className="h-3 w-3 text-slate-400" />
                    ) : (
                        <ChevronRight className="h-3 w-3 text-slate-400" />
                    )}
                    {keyName && (
                        <>
                            <span className="text-slate-700">{keyName}</span>
                            <span className="text-slate-500">: </span>
                        </>
                    )}
                    <span className="text-slate-600">
                        [{isExpanded ? '' : `${value.length} items]`}
                    </span>
                </div>
                {isExpanded && (
                    <>
                        {value.map((item, index) => (
                            <JsonNode
                                key={index}
                                value={item}
                                depth={depth + 1}
                                maxInitialDepth={maxInitialDepth}
                                keyName={String(index)}
                                onTruncatedStringClick={onTruncatedStringClick}
                            />
                        ))}
                        <div style={{ paddingLeft: indent }} className="flex items-center">
                            <span className="inline-block h-3 w-3 flex-shrink-0" />
                            <span className="text-slate-600">]</span>
                        </div>
                    </>
                )}
            </div>
        );
    }

    // Render objects
    if (typeof value === 'object') {
        const entries = Object.entries(value);
        
        if (entries.length === 0) {
            return (
                <JsonLine indent={indent} keyName={keyName}>
                    <span className="text-slate-600">{'{}'}</span>
                </JsonLine>
            );
        }

        return (
            <div>
                <div
                    className="flex cursor-pointer items-center hover:bg-slate-100"
                    onClick={() => setIsExpanded(!isExpanded)}
                    style={{ paddingLeft: indent }}
                >
                    {isExpanded ? (
                        <ChevronDown className="h-3 w-3 text-slate-400" />
                    ) : (
                        <ChevronRight className="h-3 w-3 text-slate-400" />
                    )}
                    {keyName && (
                        <>
                            <span className="text-slate-700">{keyName}</span>
                            <span className="text-slate-500">: </span>
                        </>
                    )}
                    <span className="text-slate-600">
                        {'{'}{isExpanded ? '' : `${entries.length} keys}`}
                    </span>
                </div>
                {isExpanded && (
                    <>
                        {entries.map(([key, val]) => (
                            <JsonNode
                                key={key}
                                value={val}
                                depth={depth + 1}
                                maxInitialDepth={maxInitialDepth}
                                keyName={key}
                                onTruncatedStringClick={onTruncatedStringClick}
                            />
                        ))}
                        <div style={{ paddingLeft: indent }} className="flex items-center">
                            <span className="inline-block h-3 w-3 flex-shrink-0" />
                            <span className="text-slate-600">{'}'}</span>
                        </div>
                    </>
                )}
            </div>
        );
    }

    // Fallback for unknown types
    return (
        <JsonLine indent={indent} keyName={keyName}>
            <span className="text-slate-500">{String(value)}</span>
        </JsonLine>
    );
}

interface JsonLineProps {
    indent: number;
    keyName?: string;
    children: React.ReactNode;
}

function JsonLine({ indent, keyName, children }: JsonLineProps) {
    return (
        <div style={{ paddingLeft: indent }} className="flex items-center py-0.5">
            {/* Spacer to align with chevron icon on expandable items */}
            <span className="inline-block h-3 w-3 flex-shrink-0" />
            {keyName && (
                <>
                    <span className="text-slate-700">{keyName}</span>
                    <span className="text-slate-500">: </span>
                </>
            )}
            {children}
        </div>
    );
}

